import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2, ShoppingCart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';
import { PaymentModal } from '../components/PaymentModal';

const TarifsPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(pricingCategories[0].id);
  const [selectedDuration, setSelectedDuration] = useState(0);
  const [paymentModal, setPaymentModal] = useState({ isOpen: false, item: null });

  const currentCategory = pricingCategories.find(c => c.id === selectedCategory);

  // Ces formules sont des abonnements app/coaching liés au compte client (accès
  // Communauté/Transformation/VIP) : elles passent par le tunnel Stripe natif
  // /transformation qui crée le compte + synchronise l'accès automatiquement.
  // Le paiement générique (checkout PaymentModal) reste réservé au présentiel
  // (packs séances, EMS, Scan 3D, etc.) qui ne touche jamais aux accès de l'app.
  const APP_LINKED_CATEGORIES = ['coaching-online', 'coaching-hybride', 'communaute'];

  // Helper pour déterminer le nombre max de mensualités selon le forfait
  const getMaxInstallments = (categoryId, price) => {
    // ≤ 1500 CHF : 1x uniquement (forfaits courts)
    if (price <= 1500) return 1;
    // 1500-2500 CHF : 1x ou 2x
    if (price <= 2500) return 2;
    // > 2500 CHF : 1x, 2x ou 3x (gros forfaits)
    return 3;
  };
  
  const [buyLoading, setBuyLoading] = useState(false);
  const handleBuyClick = (pkg) => {
    if (currentCategory.id === 'communaute') {
      // Formule en libre-service (49 CHF/mois, sans engagement) : paiement Stripe direct.
      setBuyLoading(true);
      setTimeout(() => navigate('/checkout/communaute'), 350);
      return;
    }
    if (APP_LINKED_CATEGORIES.includes(currentCategory.id)) {
      // Coaching Online/Hybride : nécessite un appel de découverte -> tunnel de vente dédié
      setBuyLoading(true);
      setTimeout(() => navigate('/transformation'), 350);
      return;
    }
    const totalPrice = pkg.total || pkg.price;
    const isMonthlySubscription = currentCategory.isMonthly === true;
    const allowAnnual = currentCategory.id === 'coaching-online' || currentCategory.id === 'coaching-hybride';
    setPaymentModal({
      isOpen: true,
      item: {
        name: `${currentCategory.name} - ${pkg.label || pkg.sessions}`,
        // Pour les abonnements, item.price = montant mensuel ; sinon, prix total
        price: isMonthlySubscription ? (pkg.price || totalPrice) : totalPrice,
        total: totalPrice,
        type: currentCategory.id,
        maxInstallments: getMaxInstallments(currentCategory.id, totalPrice),
        allowAnnual,
        months: isMonthlySubscription ? (pkg.sessions && parseInt(pkg.sessions)) || 6 : 1,
      }
    });
  };

  return (
    <main className="pt-32" data-testid="tarifs-page">
      <SEOHead
        title="Tarifs Personal Training Nyon | Prix Abonnements Coaching | Body-Up"
        description="Tarifs Body-Up Nyon : séances 30-60min, packs 10-40 séances. Personal Training, EMS, Duo. Dès CHF 55/séance. Consultation GRATUITE. Meilleurs prix coaching sportif Nyon."
        path="/tarifs"
      />
      <section className="py-16 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/30 rounded-full blur-[120px] animate-pulse-slow" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-pink-500/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <BackNav />
          {/* Title Section */}
          <AnimatedSection>
            <div className="text-center mb-12">
              {/* Floating Badge - WAHOU */}
              <SectionBadge emoji="💰" className="animate-float">
                {language === 'fr' ? "Nos formules d'abonnement" : "Our subscription plans"}
              </SectionBadge>
              
              <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                <span className="block">{language === 'fr' ? 'Tarifs' : 'Pricing'}</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-400 to-primary animate-gradient bg-[length:200%_auto]">
                  {language === 'fr' ? 'Personal Training' : 'Personal Training'}
                </span>
              </h1>
              
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {language === 'fr' 
                  ? 'Des formules adaptées à vos objectifs et votre rythme'
                  : 'Plans adapted to your goals and pace'}
              </p>
            </div>
          </AnimatedSection>

          {/* Category Selection - Responsive Grid (C1-UX Improvement) */}
          <AnimatedSection delay={100}>
            <div className="mb-12">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 max-w-7xl mx-auto">
                {pricingCategories.map((category, index) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSelectedDuration(0);
                    }}
                    className={`group relative px-4 py-4 sm:py-5 rounded-2xl font-semibold transition-all duration-300 text-sm sm:text-base ${
                      selectedCategory === category.id
                        ? "bg-gradient-to-br from-primary to-pink-500 text-white shadow-2xl shadow-primary/50 scale-105"
                        : "bg-white/5 backdrop-blur-md border border-white/10 text-gray-400 hover:text-white hover:border-primary/50 hover:scale-105"
                    }`}
                    data-testid={`category-${category.id}`}
                  >
                    {/* Glow effect on hover */}
                    {selectedCategory === category.id && (
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-pink-500 rounded-2xl blur-xl opacity-50 animate-pulse-slow" />
                    )}
                    
                    {/* Icon based on category */}
                    <div className="relative z-10 flex flex-col items-center gap-2">
                      <span className="text-2xl">
                        {category.id === 'personal-training' && '🏋️'}
                        {category.id === 'duo-training' && '👥'}
                        {category.id === 'ems-training' && '⚡'}
                        {category.id === 'ems-duo-training' && '⚡👥'}
                        {category.id === 'scan-3d' && '📊'}
                        {category.id === 'plan-alimentaire' && '🥗'}
                        {category.id === 'hypoxie-fitsonic' && '🏔️'}
                        {category.id === 'coaching-hybride' && '🔥'}
                        {category.id === 'coaching-online' && '💻'}
                      </span>
                      <span className="text-center leading-tight">{category.name}</span>
                      
                      {/* Active indicator */}
                      {selectedCategory === category.id && (
                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1/2 h-1 bg-white rounded-full" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* Duration Selection - Mobile Friendly with GOLD/SILVER Badges */}
          {currentCategory && currentCategory.durations.length > 1 && (
            <AnimatedSection delay={200}>
              {/* Duo Training Notice */}
              {currentCategory.isDuo && (
                <div className="flex justify-center mb-6">
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-full">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-300 font-semibold text-sm sm:text-base">
                      {language === 'fr' ? '👫 Prix par personne' : '👫 Price per person'}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex justify-center mb-12 -mx-4 px-4 overflow-x-auto scrollbar-hide">
                <div className={`inline-flex backdrop-blur-md rounded-2xl p-2 border shadow-xl min-w-max ${
                  currentCategory.hasTiers 
                    ? "bg-gradient-to-r from-gray-800/80 to-gray-900/80 border-white/30" 
                    : "bg-gradient-to-r from-white/10 to-white/5 border-white/20"
                }`}>
                  {currentCategory.durations.map((duration, index) => {
                    // Silver and Gold tier styles
                    const isSilver = duration.tier === 'silver';
                    const isGold = duration.tier === 'gold';
                    const isSelected = selectedDuration === index;
                    
                    let tierStyles = "";
                    if (isSilver) {
                      tierStyles = isSelected 
                        ? "bg-gradient-to-br from-gray-200 via-gray-100 to-gray-300 text-gray-800 shadow-2xl shadow-gray-400/50 scale-105 border-2 border-gray-400" 
                        : "bg-gradient-to-br from-gray-500/30 to-gray-600/30 text-gray-200 hover:from-gray-400/40 hover:to-gray-500/40 border border-gray-500/50";
                    } else if (isGold) {
                      tierStyles = isSelected 
                        ? "bg-gradient-to-br from-yellow-300 via-amber-400 to-yellow-500 text-gray-900 shadow-2xl shadow-yellow-500/50 scale-105 border-2 border-yellow-400" 
                        : "bg-gradient-to-br from-yellow-600/30 to-amber-600/30 text-yellow-200 hover:from-yellow-500/40 hover:to-amber-500/40 border border-yellow-500/50";
                    } else {
                      tierStyles = isSelected 
                        ? "bg-white text-gray-900 shadow-2xl scale-105" 
                        : "text-white hover:bg-white/10";
                    }
                    
                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedDuration(index)}
                        className={`relative px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-bold transition-all duration-500 flex flex-col items-center min-w-[100px] sm:min-w-[140px] ${tierStyles}`}
                        data-testid={`duration-${duration.time}`}
                      >
                        {/* Tier Icon */}
                        {(isSilver || isGold) && (
                          <span className="text-2xl mb-1">
                            {isGold ? '👑' : '🥈'}
                          </span>
                        )}
                        <span className={`text-xl sm:text-2xl font-black ${isGold && isSelected ? 'text-gray-900' : ''}`}>
                          {duration.time}
                        </span>
                        {duration.subtitle && (
                          <span className={`text-xs mt-1 ${
                            isSelected 
                              ? (isGold ? "text-gray-700" : isSilver ? "text-gray-600" : "text-gray-600") 
                              : "text-gray-400"
                          }`}>
                            {duration.subtitle}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* Pricing Cards - WAHOU Style */}
          {currentCategory && (
            <AnimatedSection delay={300}>
              {/* Duo Training Notice for single duration categories */}
              {currentCategory.isDuo && currentCategory.durations.length === 1 && (
                <div className="flex justify-center mb-8">
                  <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-full">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="text-blue-300 font-semibold">
                      {language === 'fr' ? '👫 Prix par personne' : '👫 Price per person'}
                    </span>
                  </div>
                </div>
              )}
              
              <div className={`grid gap-8 max-w-6xl mx-auto ${
                currentCategory.durations[selectedDuration]?.packages.length <= 2 
                  ? "md:grid-cols-2 max-w-3xl" 
                  : currentCategory.durations[selectedDuration]?.packages.length === 3
                    ? "md:grid-cols-3 max-w-5xl"
                    : "md:grid-cols-2 lg:grid-cols-4"
              }`}>
                {currentCategory.durations[selectedDuration]?.packages.map((pkg, index) => (
                  <div
                    key={index}
                    className={`group relative rounded-3xl transition-all duration-500 hover:-translate-y-4 ${
                      pkg.popular ? "md:-translate-y-4" : ""
                    }`}
                  >
                    {/* Card Glow Effect */}
                    <div className={`absolute -inset-1 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl ${
                      pkg.popular 
                        ? "bg-gradient-to-r from-primary via-pink-500 to-primary" 
                        : "bg-primary/50"
                    }`} />
                    
                    {/* Card Content */}
                    <div className={`relative rounded-3xl overflow-hidden ${
                      pkg.popular
                        ? "bg-gradient-to-br from-primary/40 via-gray-900 to-pink-500/30 border-2 border-primary"
                        : "bg-gradient-to-br from-white/10 to-gray-900/50 border border-white/20 hover:border-primary/50"
                    }`}>
                      
                      {/* Popular Badge */}
                      {pkg.popular && (
                        <div className="absolute top-0 left-0 right-0">
                          <div className="flex justify-center">
                            <span className="bg-gradient-to-r from-primary to-pink-500 text-white text-xs font-black px-6 py-2 rounded-b-xl shadow-lg shadow-primary/50 flex items-center gap-2">
                              <Sparkles className="w-4 h-4 animate-spin-slow" />
                              POPULAIRE
                              <Sparkles className="w-4 h-4 animate-spin-slow" />
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className={`p-8 ${pkg.popular ? "pt-14" : ""}`}>
                        {/* Package Name */}
                        <div className="text-center mb-6">
                          <h3 className="text-2xl font-bold text-white mb-2">{pkg.sessions}</h3>
                          <p className="text-gray-400">{pkg.label}</p>
                          {currentCategory.isDuo && (
                            <p className="text-blue-400 text-xs mt-1 font-semibold">/personne</p>
                          )}
                        </div>
                        
                        {/* Price */}
                        <div className="text-center mb-6">
                          <div className="flex items-baseline justify-center gap-1">
                            <span className={`text-5xl font-black ${
                              pkg.popular 
                                ? "text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400" 
                                : "text-white"
                            }`}>
                              {pkg.price}
                            </span>
                            <span className="text-xl text-gray-400 font-medium">CHF</span>
                          </div>
                          
                          {pkg.perSession && currentCategory.isSubscription && (
                            <p className="text-gray-500 text-sm mt-1">≈ {pkg.perSession} CHF/séance</p>
                          )}
                        </div>
                        
                        {/* Total Price */}
                        {pkg.total && pkg.sessions !== "1" && !currentCategory.isSubscription && !currentCategory.isMonthly && (
                          <div className="text-center mb-4">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-gray-300 text-sm">
                              Total : <span className="font-semibold text-white">{pkg.total} CHF</span>
                            </span>
                          </div>
                        )}

                        {/* Monthly subscription total */}
                        {pkg.total && currentCategory.isMonthly && (
                          <div className="text-center mb-4">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full text-gray-300 text-sm">
                              Total {pkg.sessions} : <span className="font-semibold text-white">{pkg.total} CHF</span>
                            </span>
                          </div>
                        )}
                        
                        {/* Savings Badge */}
                        {pkg.savings && (
                          <div className="flex justify-center mb-4">
                            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-full text-green-400 text-sm font-semibold animate-pulse-slow">
                              <Sparkles className="w-4 h-4" />
                              Économisez {pkg.savings}
                            </span>
                          </div>
                        )}
                        
                        {/* Features List with Validity */}
                        <ul className="space-y-3 mb-8">
                          {(pkg.includes && pkg.includes.length > 0
                            ? pkg.includes
                            : [
                                language === 'fr' ? "Coaching personnalisé" : "Personalized coaching",
                                language === 'fr' ? "Programme sur mesure" : "Custom program",
                                language === 'fr' ? "Suivi progression" : "Progress tracking",
                                pkg.validity ? (language === 'fr' ? `✨ Validité : ${pkg.validity}` : `✨ Validity: ${pkg.validity}`) : (language === 'fr' ? "Accès studio privé" : "Private studio access")
                              ]
                          ).map((feature, i) => (
                            <li key={i} className={`flex items-start gap-3 ${feature.includes && feature.includes('Validité') || (typeof feature === 'string' && feature.includes('Validity')) ? 'text-primary font-semibold' : 'text-gray-300'}`}>
                              <div className={`shrink-0 mt-1 w-5 h-5 rounded-full flex items-center justify-center ${
                                pkg.popular 
                                  ? "bg-gradient-to-r from-primary to-pink-500" 
                                  : "bg-primary/30"
                              }`}>
                                <Check className="w-3 h-3 text-white" />
                              </div>
                              <span className="text-sm leading-relaxed">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        {/* CTA Button - Buy Now */}
                        <button
                          onClick={() => handleBuyClick(pkg)}
                          disabled={buyLoading}
                          className={`w-full py-6 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-70 disabled:hover:scale-100 ${
                            pkg.popular
                              ? "bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-white shadow-lg shadow-primary/50"
                              : "bg-white hover:bg-gray-100 text-gray-900"
                          }`}
                        >
                          {buyLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShoppingCart className="w-5 h-5" />}
                          {language === 'fr' ? 'Acheter' : 'Buy Now'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}

          {/* Payment Modal */}
          <PaymentModal
            isOpen={paymentModal.isOpen}
            onClose={() => setPaymentModal({ isOpen: false, item: null })}
            item={paymentModal.item}
          />

          {/* Free Consultation CTA */}
          <AnimatedSection delay={400}>
            <div className="mt-16 text-center">
              <div className="inline-block relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary via-pink-500 to-primary rounded-3xl blur-2xl opacity-30 animate-pulse-slow" />
                <div className="relative bg-gradient-to-r from-primary/20 via-pink-500/20 to-primary/20 backdrop-blur-md rounded-2xl p-8 border border-primary/30">
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Gift className="w-8 h-8 text-primary" />
                    <span className="text-2xl font-bold text-white">
                      {language === 'fr' ? 'Consultation Gratuite' : 'Free Consultation'}
                    </span>
                  </div>
                  <p className="text-gray-300 mb-6">
                    {language === 'fr' 
                      ? 'Bilan personnalisé offert pour les nouveaux clients. Sans engagement.'
                      : 'Free personalized assessment for new clients. No commitment.'}
                  </p>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-105 transition-all">
                      {language === 'fr' ? 'Réserver ma Consultation' : 'Book my Consultation'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* FAQ Link */}
          <AnimatedSection delay={600}>
            <div className="mt-16 text-center">
              <h3 className="text-xl font-bold text-white mb-6">
                {language === 'fr' ? 'Vous avez des questions ?' : 'Have questions?'}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/faq">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                    <HelpCircle className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Consulter la FAQ' : 'View FAQ'}
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {language === 'fr' ? 'Nous contacter' : 'Contact Us'}
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Contact Page

export default TarifsPage;
